import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, FileText, Sparkles, ExternalLink } from "lucide-react";

const STATUS_TABS = ["draft", "pending_review", "approved", "scheduled", "published", "archived"];
const STATUS_LABEL = { draft: "Draft", pending_review: "Pending Review", approved: "Approved", scheduled: "Scheduled", published: "Published", archived: "Archived" };
const STATUS_COLOR = {
  draft: "bg-slate-100 text-slate-600", pending_review: "bg-amber-100 text-amber-700",
  approved: "bg-sky-100 text-sky-700", scheduled: "bg-indigo-100 text-indigo-700",
  published: "bg-emerald-100 text-emerald-700", archived: "bg-slate-200 text-slate-500",
};

export default function AdminCMS() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("draft");

  const load = useCallback(() => {
    api.get("/admin/articles").then(({ data }) => setData(data)).catch(() => setData({ items: [], counts: {}, categories: {} }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const createNew = async () => {
    try {
      const { data: a } = await api.post("/admin/articles", { title: "Untitled Article", category: "news", body_html: "" });
      window.location.href = `/admin/cms/articles/${a.id}`;
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  if (!data) return <AdminLayout><div className="p-8 label-overline">Loading CMS…</div></AdminLayout>;
  const items = (data.items || []).filter((a) => a.status === tab);

  return (
    <AdminLayout>
      <div className="p-4 sm:p-8 max-w-6xl">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 label-overline text-[#E63946]"><FileText className="w-4 h-4" /> Content</div>
            <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tighter mt-2">News & Articles</h1>
          </div>
          <button data-testid="cms-create-article" onClick={createNew} className="inline-flex items-center gap-2 bg-[#0A192F] hover:bg-[#1a2a4a] text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
            <Plus className="w-4 h-4" /> New Article
          </button>
        </div>

        <div className="flex gap-1 border-b border-slate-200 mt-6 overflow-x-auto">
          {STATUS_TABS.map((s) => (
            <button key={s} data-testid={`cms-tab-${s}`} onClick={() => setTab(s)}
              className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wider whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === s ? "border-[#E63946] text-[#0A192F]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
              {STATUS_LABEL[s]} <span className="ml-1 text-slate-400">({data.counts?.[s] || 0})</span>
            </button>
          ))}
        </div>

        <div className="mt-5 space-y-2">
          {items.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-sm p-10 text-center text-sm text-slate-500">No {STATUS_LABEL[tab].toLowerCase()} articles.</div>
          ) : items.map((a) => (
            <Link key={a.id} to={`/admin/cms/articles/${a.id}`} data-testid={`cms-article-${a.id}`}
              className="bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-4 hover:border-[#0A192F] transition-colors">
              <div className="w-14 h-14 rounded-sm bg-slate-100 overflow-hidden shrink-0">
                {a.cover_image_url ? <img src={a.cover_image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-slate-300"><FileText className="w-5 h-5" /></div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[9px] uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm">{data.categories?.[a.category] || a.category}</span>
                  {a.is_auto_generated && <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider bg-[#E63946]/10 text-[#E63946] px-1.5 py-0.5 rounded-sm"><Sparkles className="w-3 h-3" /> Auto</span>}
                </div>
                <div className="font-semibold mt-1 truncate">{a.title}</div>
                <div className="text-[11px] text-slate-400">by {a.author_name} · {new Date(a.updated_at).toLocaleDateString("en-CA")}</div>
              </div>
              <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${STATUS_COLOR[a.status]}`}>{STATUS_LABEL[a.status]}</span>
            </Link>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
